export interface ParsedDocument {
  text: string
  kind: 'text' | 'pdf' | 'docx' | 'xlsx'
  truncated: boolean
}

const MAX_EXTRACTED_CHARS = 200_000

function capText(text: string): ParsedDocument['text'] {
  return text.length > MAX_EXTRACTED_CHARS ? text.slice(0, MAX_EXTRACTED_CHARS) : text
}

function finish(text: string, kind: ParsedDocument['kind']): ParsedDocument {
  const normalized = text.replace(/\u0000/g, '').trim()
  return {
    text: capText(normalized),
    kind,
    truncated: normalized.length > MAX_EXTRACTED_CHARS,
  }
}

async function parsePdf(file: File): Promise<ParsedDocument> {
  const pdfjs = await import('pdfjs-dist/legacy/build/pdf.mjs')
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/legacy/build/pdf.worker.mjs',
    import.meta.url,
  ).toString()

  const data = new Uint8Array(await file.arrayBuffer())
  const pdf = await pdfjs.getDocument({ data }).promise
  const pages: string[] = []

  for (let pageNo = 1; pageNo <= pdf.numPages; pageNo += 1) {
    const page = await pdf.getPage(pageNo)
    const content = await page.getTextContent()
    const line = content.items
      .map((item) => ('str' in item ? item.str : ''))
      .filter(Boolean)
      .join(' ')
    pages.push(`--- Page ${pageNo} ---\n${line}`)
    if (pages.join('\n\n').length >= MAX_EXTRACTED_CHARS) break
  }

  return finish(pages.join('\n\n'), 'pdf')
}

async function parseDocx(file: File): Promise<ParsedDocument> {
  const mammoth = await import('mammoth')
  const result = await mammoth.extractRawText({ arrayBuffer: await file.arrayBuffer() })
  return finish(result.value || '', 'docx')
}

async function parseXlsx(file: File): Promise<ParsedDocument> {
  const xlsx = await import('read-excel-file/browser')
  const sheets = await xlsx.default(file)
  const out: string[] = []
  let charCount = 0

  for (const sheet of sheets) {
    const heading = `--- Sheet: ${sheet.sheet} ---`
    out.push(heading)
    charCount += heading.length + 1
    for (const row of sheet.data) {
      const line = row.map((cell) => cell == null ? '' : String(cell)).join('\t')
      out.push(line)
      charCount += line.length + 1
      if (charCount >= MAX_EXTRACTED_CHARS) break
    }
    if (charCount >= MAX_EXTRACTED_CHARS) break
  }

  return finish(out.join('\n'), 'xlsx')
}

export async function extractDocumentText(file: File): Promise<ParsedDocument> {
  const ext = file.name.split('.').pop()?.toLowerCase() || ''
  const mime = file.type.toLowerCase()

  if (mime === 'application/pdf' || ext === 'pdf') return parsePdf(file)
  if (
    mime === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    ext === 'docx'
  ) return parseDocx(file)
  if (
    mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
    ext === 'xlsx'
  ) return parseXlsx(file)

  return finish(await file.text(), 'text')
}
